import React, { useState } from "react";
import { Box, FormHelperText, Grid2, Typography } from "@mui/material";
import {
  CurrentValueBox,
  MAX_REMARK_LENGTH,
  RemarkField,
  StyledTextField,
  ViewHeader,
} from "./EditDialogShared";
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import { useToast } from "../../Hooks/useToast";
import { useBatteryInfo, useVehicleInfo } from "../../Pages/DigitalTwinV3/DigitalTwinContext";
import { updateVehicleChassisNumber } from "../../Api/VehicleApi";
import { translateErrorMessage } from "../../Helper/Common/FieldTranslator";

export default function ChangeChassisNumberView({ onBack, onSuccess }) {
  const { openNotification } = useToast();
  const { batteryID } = useBatteryInfo();
  const { vin: currentChassisNumber } = useVehicleInfo();

  const [newChassisNumber, setNewChassisNumber] = useState("");
  const [remark, setRemark] = useState("");
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async () => {
    const errs = {};
    if (!newChassisNumber.trim()) {
      errs.chassisNumber = "Please enter a chassis number";
    } else if (newChassisNumber.trim().length !== 17) {
      errs.chassisNumber = "Chassis Number must be exactly 17 characters long";
    }
    if (!remark.trim()) errs.remark = true;
    if (remark.length > MAX_REMARK_LENGTH) errs.remarkCount = true;

    setErrors(errs);
    if (Object.keys(errs).length) return;

    setIsLoading(true);
    try {
      const response = await updateVehicleChassisNumber(batteryID, {
        vin: newChassisNumber,
        remarks: remark,
      });

      if (response?.responseStatus?.code !== 200) {
        throw new Error(response?.responseStatus?.msg);
      }

      await onSuccess("Chassis Number Updated!");
    } catch (error) {
      const msg = translateErrorMessage(error?.message);
      setErrors({ chassisNumber: msg });
      openNotification({ text: msg, icon: "toast-error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <ViewHeader title="Change Chassis Number" onBack={onBack} />

      <CurrentValueBox label="Current Chassis Number" value={currentChassisNumber} />

      <Grid2 container flexDirection="column" mb={1.5} gap={0.5}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="fw400s14c000000"
            sx={{ lineHeight: "24px", color: "#494949" }}
          >
            New Chassis Number
          </Typography>
          <Typography
            variant="fw400s14c000000"
            sx={{ fontWeight: 700, lineHeight: "24px", color: "#B50500" }}
          >
            &nbsp;*
          </Typography>
        </Box>
        <StyledTextField
          fullWidth
          autoComplete="off"
          variant="outlined"
          placeholder="Type here"
          value={newChassisNumber}
          onChange={(e) => setNewChassisNumber(e.target.value)}
          error={!!errors.chassisNumber}
        />
        {errors.chassisNumber && (
          <FormHelperText sx={{ fontSize: "10px", color: "#930500", ml: 0, mt: -0.5 }}>
            {typeof errors.chassisNumber === "string"
              ? errors.chassisNumber
              : "Please enter a chassis number"}
          </FormHelperText>
        )}
      </Grid2>

      <RemarkField
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
        error={errors.remark}
      />

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryFormLoadingActionButton
          disabled={isLoading}
          onClick={handleSubmit}
          loading={isLoading}
          sx={{ borderRadius: "4px", px: 3 }}
        >
          Confirm Change
        </PrimaryFormLoadingActionButton>
      </Box>
    </Box>
  );
}
